import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import SeoHead from "@/components/public/SeoHead";
import FadeIn from "@/components/public/FadeIn";
import { MapPin, BookOpen, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PlanDuSitePage() {
  const [services, setServices] = useState([]);
  const [villes, setVilles] = useState([]);
  const [seoPages, setSeoPages] = useState([]);
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/services`),
      axios.get(`${API}/villes`),
      axios.get(`${API}/pages-seo`),
      axios.get(`${API}/blog?limit=20`),
    ]).then(([s, v, p, b]) => {
      setServices(s.data);
      setVilles(v.data);
      setSeoPages(p.data.filter(pg => pg.is_active));
      setArticles(b.data.articles || []);
    });
  }, []);

  const b2c = services.filter(s => s.type_client === "B2C" || s.type_client === "mixte");
  const b2b = services.filter(s => s.type_client === "B2B");

  return (
    <div data-testid="plan-du-site-page">
      <SeoHead
        title="Plan du site"
        description="Plan du site Nettoyage Expert. Retrouvez tous nos services de nettoyage professionnel en Suisse romande : canapés, vitres, terrasses, moquettes et plus."
        path="/plan-du-site"
        breadcrumbs={[
          { name: "Accueil", url: "/" },
          { name: "Plan du site" }
        ]}
      />

      {/* Hero */}
      <section className="bg-[#336DBF] py-14 sm:py-18">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white tracking-tight">
            Plan du site
          </h1>
          <p className="mt-3 text-white/75 text-lg">Toutes les pages de Nettoyage Expert</p>
        </div>
      </section>

      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-14">

          {/* Pages principales */}
          <FadeIn>
            <h2 className="text-xl sm:text-2xl font-semibold text-[#062133] tracking-tight mb-5">Pages principales</h2>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {[
                { name: "Accueil", to: "/" },
                { name: "Services", to: "/services" },
                { name: "Contrats entreprises", to: "/contrats-entreprises" },
                { name: "Avis clients", to: "/avis-clients" },
                { name: "Blog", to: "/blog" },
                { name: "À propos", to: "/a-propos" },
                { name: "Contact", to: "/contact" },
              ].map(p => (
                <li key={p.to}>
                  <Link to={p.to} className="flex items-center gap-2 px-4 py-2.5 rounded-md hover:bg-[#336DBF]/5 text-sm font-medium text-[#336DBF] transition-colors" data-testid={`sitemap-link-${p.to.replace(/\//g, "")}`}>
                    <ArrowRight size={14} className="shrink-0" /> {p.name}
                  </Link>
                </li>
              ))}
            </ul>
          </FadeIn>

          {/* Services */}
          <FadeIn>
            <h2 className="text-xl sm:text-2xl font-semibold text-[#062133] tracking-tight mb-5">Nos services</h2>
            {b2c.length > 0 && (
              <div className="mb-6">
                <h3 className="text-base font-semibold text-[#062133]/80 mb-3">Pour les particuliers</h3>
                <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {b2c.map(s => (
                    <li key={s.slug}>
                      <Link to={`/services/${s.slug}`} className="flex items-center gap-2 px-4 py-2.5 rounded-md hover:bg-[#336DBF]/5 text-sm text-[#336DBF] transition-colors">
                        <ArrowRight size={14} className="shrink-0" /> {s.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {b2b.length > 0 && (
              <div>
                <h3 className="text-base font-semibold text-[#062133]/80 mb-3">Pour les entreprises</h3>
                <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {b2b.map(s => (
                    <li key={s.slug}>
                      <Link to={`/services/${s.slug}`} className="flex items-center gap-2 px-4 py-2.5 rounded-md hover:bg-[#336DBF]/5 text-sm text-[#336DBF] transition-colors">
                        <ArrowRight size={14} className="shrink-0" /> {s.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </FadeIn>

          {/* Villes */}
          {villes.length > 0 && (
            <FadeIn>
              <h2 className="text-xl sm:text-2xl font-semibold text-[#062133] tracking-tight mb-5">Nettoyage par ville</h2>
              <div className="space-y-6">
                {villes.map(v => {
                  const villePages = seoPages.filter(p => p.ville_name === v.name);
                  if (villePages.length === 0) return null;
                  return (
                    <div key={v.slug}>
                      <h3 className="flex items-center gap-2 text-base font-semibold text-[#062133]/80 mb-2">
                        <MapPin size={16} className="text-[#336DBF]" /> {v.name}
                        <span className="text-xs font-normal text-[#062133]/40">{v.canton}</span>
                      </h3>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1">
                        {villePages.map(p => (
                          <li key={p.slug}>
                            <Link to={`/${p.slug}`} className="flex items-center gap-2 px-4 py-2 rounded-md hover:bg-[#336DBF]/5 text-sm text-[#336DBF] transition-colors">
                              <ArrowRight size={12} className="shrink-0 opacity-40" /> {p.service_name} à {p.ville_name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </FadeIn>
          )}

          {/* Blog */}
          {articles.length > 0 && (
            <FadeIn>
              <h2 className="text-xl sm:text-2xl font-semibold text-[#062133] tracking-tight mb-5">
                <span className="flex items-center gap-2"><BookOpen size={20} className="text-[#336DBF]" /> Blog — Derniers articles</span>
              </h2>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {articles.map(a => (
                  <li key={a.slug}>
                    <Link to={`/blog/${a.slug}`} className="flex items-start gap-2 px-4 py-2.5 rounded-md hover:bg-[#336DBF]/5 text-sm text-[#336DBF] transition-colors">
                      <ArrowRight size={14} className="shrink-0 mt-0.5" />
                      <span className="line-clamp-1">{a.title}</span>
                    </Link>
                  </li>
                ))}
              </ul>
              <Link to="/blog" className="inline-flex items-center gap-1 mt-4 text-sm font-semibold text-[#336DBF] hover:underline">
                Voir tous les articles <ArrowRight size={14} />
              </Link>
            </FadeIn>
          )}
        </div>
      </section>
    </div>
  );
}
